import createClient, { type Client, type Middleware } from 'openapi-fetch';
import type { components, paths } from './schema';

export type { components, paths };

/** Формат ошибок API: `{ error: { code, message, params, requestId } }` (docs/contracts/backend-conventions.md). */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    params: Record<string, unknown>;
    requestId: string | null;
  };
}

/** Ошибка API с кодом для перевода на клиенте (`errors.<CODE>`). */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly params: Record<string, unknown>;
  readonly requestId: string | null;

  constructor(status: number, body: ApiErrorBody | null) {
    super(body?.error.message ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error.code ?? (status === 0 ? 'NETWORK_ERROR' : `HTTP_${status}`);
    this.params = body?.error.params ?? {};
    this.requestId = body?.error.requestId ?? null;
  }
}

export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ApiErrorBody).error === 'object' &&
    typeof (value as ApiErrorBody).error?.code === 'string'
  );
}

export interface DodocClientOptions {
  /** Адрес бэкенда без пути API: `https://api.dodoc.kz` или `http://localhost:8080`. */
  baseUrl: string;
  /** Текущий access-токен (или null для открытых маршрутов). */
  getAccessToken?: () => string | null | undefined | Promise<string | null | undefined>;
  /** Выбранная клиника — заголовок X-Clinic-Id для маршрутов данных клиники. */
  getClinicId?: () => string | null | undefined;
  /** Язык интерфейса — заголовок X-Locale (ru | kk). */
  getLocale?: () => 'ru' | 'kk';
  /** Вызывается при 401 (например, чтобы обновить токен по refresh или выйти). */
  onUnauthorized?: (error: ApiError) => void | Promise<void>;
  fetch?: (input: Request) => Promise<Response>;
}

export type DodocClient = Client<paths>;

/**
 * Типизированный клиент API: пути и типы — из OpenAPI бэкенда (`npm run generate`).
 *
 * ```ts
 * const api = createDodocClient({ baseUrl, getAccessToken: () => auth.token, getClinicId: () => clinic.id, getLocale: () => i18n.language });
 * const page = unwrap(await api.GET('/api/v1/audit/events', { params: { query: { limit: 50 } } }));
 * ```
 */
export function createDodocClient(options: DodocClientOptions): DodocClient {
  const client = createClient<paths>({ baseUrl: options.baseUrl.replace(/\/+$/, ''), fetch: options.fetch });
  const middleware: Middleware = {
    async onRequest({ request }) {
      const token = await options.getAccessToken?.();
      if (token) request.headers.set('Authorization', `Bearer ${token}`);
      const clinicId = options.getClinicId?.();
      if (clinicId && !request.headers.has('X-Clinic-Id')) request.headers.set('X-Clinic-Id', clinicId);
      const locale = options.getLocale?.();
      if (locale) request.headers.set('X-Locale', locale);
      return request;
    },
    async onResponse({ response }) {
      if (response.status === 401 && options.onUnauthorized) {
        const body = (await response.clone().json().catch(() => null)) as unknown;
        await options.onUnauthorized(new ApiError(401, isApiErrorBody(body) ? body : null));
      }
      return response;
    },
  };
  client.use(middleware);
  return client;
}

/** Данные успешного ответа или ApiError. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || !result.response.ok) {
    throw new ApiError(result.response.status, isApiErrorBody(result.error) ? result.error : null);
  }
  return result.data as T;
}

/**
 * Адрес потока событий SSE для EventSource (токен и клиника передаются в query,
 * потому что EventSource не умеет заголовки): `new EventSource(eventStreamUrl(...))`.
 * @deprecated TD-06: токен в строке запроса принимается только в переходный период (SSE_ALLOW_QUERY_TOKEN).
 * Открывайте поток по одноразовому билету: `POST /api/v1/events/ticket` → `…/events/stream?ticket=…`
 * (веб — `web/src/app/api/eventStream.ts`).
 */
export function eventStreamUrl(baseUrl: string, accessToken: string, clinicId?: string | null): string {
  const url = new URL('/api/v1/events/stream', baseUrl);
  url.searchParams.set('access_token', accessToken);
  if (clinicId) url.searchParams.set('clinicId', clinicId);
  return url.toString();
}
